/**
 * Geräteaktivierung V2 – Geräteseite des Vertrags mit der API.
 *
 * Grundlage: `docs/geraeteaktivierung/vertrag.md` Version 1 und
 * `packages/api/src/geraeteaktivierung/vertrag.ts` (AV1, Branch `av1-backend`, Commit d958988).
 * Die Typen unten sind ein **Spiegel** der Gerätetypen von dort, weil dieser Branch die Datei
 * noch nicht enthält; nach dem Zusammenführen ersetzt der Koordinator sie durch `import type`
 * (Änderungswunsch AV3-1). Alles Wissen über Pfade, Feldnamen und Fehlerkennzeichen steht nur
 * hier; der Zustandsautomat kennt nur `AktivierungsSchnittstelle`.
 *
 * Muster OAuth 2.0 Device Authorization Grant (RFC 8628):
 * 1. `POST /geraeteaktivierung/start` → `geraeteGeheimnis` (nur in dieser Antwort), Benutzercode,
 *    QR-Inhalt, Ablauf, Intervall.
 * 2. `POST /geraeteaktivierung/abholen {geraeteGeheimnis}` im Intervall (Abfrage UND Abholung).
 * 3. Im Zustand `bestaetigt` erzeugt das Gerät den Kassenschlüssel `<kassenId>.<geheim>` selbst
 *    und sendet nur `schluesselHash` (SHA-256 hex) – der Schlüssel reist nie über das Netz.
 * 4. `abgeholt` mit `kassenschluesselGesetzt: true` → Schlüssel gilt.
 */

export const AKTIVIERUNG_PFAD = '/geraeteaktivierung';
export const VERTRAG_VERSION = 1;

/** Grenzwerte aus dem Vertrag (Spiegel). */
export const SITZUNG_GUELTIG_S = 10 * 60;
export const INTERVALL_START_S = 5;
export const INTERVALL_AUFSCHLAG_S = 5;
export const INTERVALL_MAX_S = 60;
export const GEHEIMNIS_MUSTER = /^[A-Za-z0-9_-]{43}$/;

export type SitzungZustand =
  'wartend' | 'zugeordnet' | 'bestaetigt' | 'abgeholt' | 'abgelaufen' | 'abgebrochen' | 'gesperrt';

const ZUSTAENDE: readonly string[] = [
  'wartend',
  'zugeordnet',
  'bestaetigt',
  'abgeholt',
  'abgelaufen',
  'abgebrochen',
  'gesperrt',
];

export type Plattform = 'web' | 'android' | 'sunmi' | 'windows' | 'ios' | 'sonstige';

/** Angaben des Geräts – nur zur Anzeige im Backoffice, ohne Geheimnisse. */
export interface GeraeteInfo {
  plattform: Plattform;
  /** ≤ 80 Zeichen */
  modell?: string;
  /** ≤ 40 Zeichen */
  appVersion?: string;
  /** ≤ 60 Zeichen */
  namensvorschlag?: string;
}

/** Stufe 2 (vorbereitet): öffentlicher Geräteschlüssel, JWK P-256. */
export interface OeffentlicherGeraeteschluessel {
  kty: 'EC';
  crv: 'P-256';
  x: string;
  y: string;
}

export interface StartAnfrage {
  geraet: GeraeteInfo;
  oeffentlicherSchluessel?: OeffentlicherGeraeteschluessel;
}

export interface StartAntwort {
  vertrag: 1;
  /** Nur hier; sicher halten, nie anzeigen oder loggen. */
  geraeteGeheimnis: string;
  benutzerCode: string;
  qrInhalt: string;
  ablaufAm: string;
  intervallSekunden: number;
}

export type AbholenAntwort =
  | { zustand: 'wartend'; ablaufAm: string; intervallSekunden: number }
  | {
      zustand: 'zugeordnet';
      bestaetigungsCode: string;
      unternehmerName: string;
      kassenName: string;
      ablaufAm: string;
      intervallSekunden: number;
    }
  | {
      zustand: 'bestaetigt';
      kassenId: string;
      unternehmerName: string;
      ablaufAm: string;
      intervallSekunden: number;
    }
  | {
      zustand: 'abgeholt';
      kassenId: string;
      geraetId: string;
      unternehmerName: string;
      kassenschluesselGesetzt: boolean;
    }
  | { zustand: 'abgelaufen' | 'abgebrochen' | 'gesperrt' };

/** Abfrageergebnis: Serverantwort oder „langsamer“ (429 `slow_down` bzw. `zuVieleVersuche`). */
export type AbholenErgebnis =
  AbholenAntwort | { zustand: 'langsamer'; intervallSekunden?: number; warteSekunden?: number };

export interface AktivierungsSchnittstelle {
  starte(anfrage: StartAnfrage): Promise<StartAntwort>;
  abholen(geraeteGeheimnis: string, schluesselHash?: string): Promise<AbholenErgebnis>;
}

/**
 * Fehler der Aktivierungsschnittstelle.
 * - `netz`: keine oder unklare Antwort (offline, Zeitüberschreitung, 5xx) – später erneut.
 * - `abgelehnt`: der Server lehnt endgültig ab (4xx außer 429); `kennzeichen`/`zustand`, falls
 *   er sie nennt (404 `unbekannt` = Sitzung unbekannt oder abgelaufen).
 * - `begrenzt`: 429 beim Start (`zuVieleVersuche`), `warteSekunden` aus Retry-After.
 */
export class AktivierungsFehler extends Error {
  override readonly name = 'AktivierungsFehler';

  constructor(
    readonly art: 'netz' | 'abgelehnt' | 'begrenzt',
    meldung: string,
    readonly status = 0,
    readonly kennzeichen: string | null = null,
    readonly zustand: SitzungZustand | null = null,
    readonly warteSekunden: number | null = null,
  ) {
    super(meldung);
  }
}

function istRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function text(x: unknown, max = 500): string | null {
  return typeof x === 'string' && x.length > 0 && x.length <= max ? x : null;
}

function sekunden(x: unknown, max = 3600): number | undefined {
  return typeof x === 'number' && Number.isFinite(x) && x > 0 && x <= max ? x : undefined;
}

function zeitpunkt(x: unknown): string | null {
  const t = text(x, 40);
  return t !== null && Number.isFinite(Date.parse(t)) ? t : null;
}

export function istBenutzerCode(code: string): boolean {
  return /^\d{8}$/.test(code);
}

export function istBestaetigungsCode(code: string): boolean {
  return /^\d{6}$/.test(code);
}

function ungueltig(): AktivierungsFehler {
  return new AktivierungsFehler('abgelehnt', 'Ungültige Antwort des Servers');
}

/** Prüft die Startantwort, damit nie Fremdes angezeigt, gespeichert oder als QR ausgegeben wird. */
export function pruefeStartAntwort(x: unknown): StartAntwort {
  if (!istRecord(x)) {
    throw ungueltig();
  }
  const geheimnis = text(x.geraeteGeheimnis, 43);
  const code = text(x.benutzerCode, 8);
  const qr = typeof x.qrInhalt === 'string' && x.qrInhalt.length <= 500 ? x.qrInhalt : null;
  const ablaufAm = zeitpunkt(x.ablaufAm);
  if (
    x.vertrag !== VERTRAG_VERSION ||
    geheimnis === null ||
    !GEHEIMNIS_MUSTER.test(geheimnis) ||
    code === null ||
    !istBenutzerCode(code) ||
    qr === null ||
    ablaufAm === null
  ) {
    throw ungueltig();
  }
  return {
    vertrag: 1,
    geraeteGeheimnis: geheimnis,
    benutzerCode: code,
    qrInhalt: qr,
    ablaufAm,
    intervallSekunden: sekunden(x.intervallSekunden, INTERVALL_MAX_S) ?? INTERVALL_START_S,
  };
}

export function pruefeAbholenAntwort(x: unknown): AbholenAntwort {
  if (!istRecord(x) || typeof x.zustand !== 'string' || !ZUSTAENDE.includes(x.zustand)) {
    throw ungueltig();
  }
  const zustand = x.zustand as SitzungZustand;
  const intervall = sekunden(x.intervallSekunden, INTERVALL_MAX_S) ?? INTERVALL_START_S;
  switch (zustand) {
    case 'wartend': {
      const ablaufAm = zeitpunkt(x.ablaufAm);
      if (ablaufAm === null) {
        throw ungueltig();
      }
      return { zustand, ablaufAm, intervallSekunden: intervall };
    }
    case 'zugeordnet': {
      const code = text(x.bestaetigungsCode, 6);
      const u = text(x.unternehmerName, 200);
      const k = text(x.kassenName, 200);
      const ablaufAm = zeitpunkt(x.ablaufAm);
      if (
        code === null ||
        !istBestaetigungsCode(code) ||
        u === null ||
        k === null ||
        ablaufAm === null
      ) {
        throw ungueltig();
      }
      return {
        zustand,
        bestaetigungsCode: code,
        unternehmerName: u,
        kassenName: k,
        ablaufAm,
        intervallSekunden: intervall,
      };
    }
    case 'bestaetigt': {
      const kassenId = text(x.kassenId, 100);
      const u = text(x.unternehmerName, 200);
      const ablaufAm = zeitpunkt(x.ablaufAm);
      if (kassenId === null || u === null || ablaufAm === null) {
        throw ungueltig();
      }
      return { zustand, kassenId, unternehmerName: u, ablaufAm, intervallSekunden: intervall };
    }
    case 'abgeholt': {
      const kassenId = text(x.kassenId, 100);
      const geraetId = text(x.geraetId, 200);
      const u = text(x.unternehmerName, 200);
      if (
        kassenId === null ||
        geraetId === null ||
        u === null ||
        typeof x.kassenschluesselGesetzt !== 'boolean'
      ) {
        throw ungueltig();
      }
      return {
        zustand,
        kassenId,
        geraetId,
        unternehmerName: u,
        kassenschluesselGesetzt: x.kassenschluesselGesetzt,
      };
    }
    case 'abgelaufen':
    case 'abgebrochen':
    case 'gesperrt':
      return { zustand };
  }
}

type Rohantwort =
  | { art: 'ok'; json: unknown }
  | {
      art: 'langsamer';
      kennzeichen: string | null;
      intervallSekunden: number | undefined;
      warteSekunden: number | undefined;
    };

/** HTTP-Umsetzung der Schnittstelle (Routen laut Vertrag Version 1). */
export class HttpAktivierung implements AktivierungsSchnittstelle {
  private readonly basisUrl: string;
  private readonly fetchFn: typeof fetch;
  private readonly zeitlimitMs: number;

  constructor(opt: { basisUrl: string; fetchFn?: typeof fetch; zeitlimitMs?: number }) {
    this.basisUrl = opt.basisUrl.replace(/\/+$/, '');
    this.fetchFn = opt.fetchFn ?? ((eingabe, init) => fetch(eingabe, init));
    this.zeitlimitMs = opt.zeitlimitMs ?? 15_000;
  }

  async starte(anfrage: StartAnfrage): Promise<StartAntwort> {
    const r = await this.sende('start', anfrage);
    if (r.art === 'langsamer') {
      throw new AktivierungsFehler(
        'begrenzt',
        'Zu viele Versuche',
        429,
        r.kennzeichen,
        null,
        r.warteSekunden ?? null,
      );
    }
    return pruefeStartAntwort(r.json);
  }

  async abholen(geraeteGeheimnis: string, schluesselHash?: string): Promise<AbholenErgebnis> {
    const r = await this.sende('abholen', {
      geraeteGeheimnis,
      ...(schluesselHash === undefined ? {} : { schluesselHash }),
    });
    if (r.art === 'langsamer') {
      return {
        zustand: 'langsamer',
        ...(r.intervallSekunden === undefined ? {} : { intervallSekunden: r.intervallSekunden }),
        ...(r.warteSekunden === undefined ? {} : { warteSekunden: r.warteSekunden }),
      };
    }
    return pruefeAbholenAntwort(r.json);
  }

  private async sende(schritt: 'start' | 'abholen', body: unknown): Promise<Rohantwort> {
    const abbruch = new AbortController();
    const uhr = setTimeout(() => {
      abbruch.abort();
    }, this.zeitlimitMs);
    let res: Response;
    let roh: string;
    try {
      res = await this.fetchFn(`${this.basisUrl}${AKTIVIERUNG_PFAD}/${schritt}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: abbruch.signal,
        // Ohne Anmeldung und ohne Cookies: die Aktivierung braucht keine Sitzung
        credentials: 'omit',
      });
      roh = await res.text();
    } catch {
      throw new AktivierungsFehler('netz', 'Server nicht erreichbar');
    } finally {
      clearTimeout(uhr);
    }
    let json: unknown = null;
    try {
      json = roh === '' ? null : JSON.parse(roh);
    } catch {
      // kein JSON
    }
    if (res.ok) {
      return { art: 'ok', json };
    }
    const daten = istRecord(json) ? json : {};
    const fehler = text(daten.fehler, 300);
    // Vertrag: Kennzeichen „neben `fehler`“ – Feldname noch offen (AV3-2), daher tolerant
    const kennzeichen = text(daten.kennzeichen, 60) ?? text(daten.code, 60) ?? fehler;
    if (res.status === 429) {
      return {
        art: 'langsamer',
        kennzeichen,
        intervallSekunden: sekunden(daten.intervallSekunden, INTERVALL_MAX_S),
        warteSekunden: sekunden(Number(res.headers.get('Retry-After'))),
      };
    }
    if (res.status >= 500) {
      throw new AktivierungsFehler('netz', 'Server vorübergehend nicht verfügbar', res.status);
    }
    const zustand =
      typeof daten.zustand === 'string' && ZUSTAENDE.includes(daten.zustand)
        ? (daten.zustand as SitzungZustand)
        : null;
    throw new AktivierungsFehler(
      'abgelehnt',
      fehler ?? `HTTP ${String(res.status)}`,
      res.status,
      kennzeichen,
      zustand,
    );
  }
}
